import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { Results } from '@elastic/react-search-ui';
import { SearchResult } from '@elastic/search-ui';
import ResultsStyled from './styled';
import theme from '../../constants/theme';
import Img from '../../assets/img/arrow.svg';
import { useIsWhichSize } from '../../hooks/useIsWhichSize';
import { device } from '../../constants/devices';
import { scrollIntoView } from '../../utils/scrollIntoView';
import { t } from '../../utils/translate';
import { FilterContext } from '../../context/filter';
import { FilterChips } from '../filter/sections/chips';
import { Selected } from '../filter/props';

type Props = {
  total: number;
  refResults: React.RefObject<HTMLDivElement>;
  refFilterHeader: React.RefObject<HTMLDivElement>;
};

export const ResultsBody = ({ total, refResults, refFilterHeader }: Props) => {
  const { isMobile } = useIsWhichSize();
  const setFilter = (window as any).setFilter;
  const removeFilter = (window as any).removeFilter;
  const setSearchTerm = (window as any).setSearchTerm;
  const hasResults = total > 0;
  const [filterIsInView, setFilterIsInView] = useState(false);
  const context = useContext(FilterContext);
  const {
    department,
    setDepartment,
    brand,
    setBrand,
    locations,
    setLocations,
    categories,
    setCategories,
    removeChips,
    removeAllChips,
    setTerm,
    setLocation,
    term,
    location,
  } = context;
  const filterCategories = [
    {
      section: department,
      action: setDepartment,
    },
    {
      section: categories,
      action: setCategories,
    },
    {
      section: locations,
      action: setLocations,
    },
    {
      section: brand,
      action: setBrand,
    },
  ];

  const setFilterInUrl = (arr: Array<Selected>, name: string) => {
    arr.length
      ? setFilter(
          name,
          arr.map((a) => a.value),
          'all'
        )
      : removeFilter(name);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => setFilterIsInView(entry.isIntersecting)),
      {
        threshold: 0,
      }
    );

    if (refFilterHeader.current) {
      observer.observe(refFilterHeader.current);
    }

    return () => {
      if (refFilterHeader.current) {
        observer.unobserve(refFilterHeader.current);
      }
    };
  }, [refFilterHeader]);

  const removeAllFilterSections = () => {
    removeFilter(department.facet);
    removeFilter(categories.facet);
    removeFilter(locations.facet);
    removeFilter(brand.facet);
  };

  const clearAllFilter = () => {
    removeFilter('search_radius');
    removeFilter('search_location');
    removeFilter('location_geopoint');
    setSearchTerm('');
    setTerm({ ...term, value: '' });
    setLocation({ ...location, value: '' });
  };

  const removeAll = () => {
    removeAllChips();
    removeAllFilterSections();
  };

  return (
    <ResultsStyled ref={refResults}>
      <Box
        display="flex"
        justifyContent="space-between"
        sx={{ cursor: 'default' }}
      >
        {(window as any).showInitiative ? (
          <Box>
            <Typography
              variant="h3"
              color={theme.palette.error.main}
              fontWeight={500}
            >
              {t('Diese Stellenausschreibung gibt es leider nicht mehr.')}
            </Typography>
            <Typography
              variant="h3"
              color={theme.palette.text.secondary}
              sx={{
                marginTop: '24px',
                marginBottom: '50px',
                letterSpacing: '0.48px',
              }}
            >
              {t(
                'Aber wir haben noch viele weitere interessante Jobs – starte einfach eine neue Suche! Oder schicke uns gerne Deine Initiativbewerbung:'
              )}
            </Typography>
          </Box>
        ) : (
          <Box display="flex">
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{ marginRight: '10px', color: '#0F8345' }}
            >
              {total || 0}
            </Typography>
            <Typography variant="h3" color={theme.palette.text.secondary}>
              {t('Stellenangebote gefunden')}
            </Typography>
          </Box>
        )}

        {!isMobile && (!hasResults || term.value || location.value) ? (
          <Box
            onClick={clearAllFilter}
            paddingRight="16px"
            sx={{ cursor: 'pointer' }}
          >
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ textDecoration: 'underline' }}
            >
              {t('Suche zurücksetzen')}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {isMobile ? (
        <Box>
          {!filterIsInView && (
            <Box mt={1} mb={3} onClick={() => scrollIntoView(refFilterHeader)}>
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight={500}
                sx={{ textDecoration: 'underline', letterSpacing: '0.28px' }}
              >
                {t('Suche anpassen')}
              </Typography>
            </Box>
          )}

          <Box mt={3}>
            {filterCategories.map((category, index) =>
              category.section.filtered?.length ? (
                <Box key={index}>
                  <FilterChips
                    arr={category.section.filtered}
                    removeChips={(label) => {
                      const arr = removeChips(
                        category.section.arr,
                        category.action,
                        false,
                        label
                      );
                      setFilterInUrl(arr, category.section.facet);
                    }}
                  />
                </Box>
              ) : null
            )}

            {department.arr.length ||
            categories.arr.length ||
            locations.arr.length ||
            brand.arr.length ? (
              <Box mt={1} mb={3} onClick={removeAll}>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  color="primary"
                  sx={{
                    textDecoration: 'underline',
                  }}
                >
                  {t('Alle Filter löschen')}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}

      {hasResults ? (
        <Results
          titleField="name"
          urlField="ref"
          shouldTrackClickThrough
          resultView={({
            result,
            onClickLink,
          }: {
            result: SearchResult;
            onClickLink: () => void;
          }) => (
            <Box className="result__wrapper" onClick={onClickLink}>
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="h4"
                  color="primary"
                  sx={{ [device.mobile]: { fontSize: '15px' } }}
                >
                  {result.name?.raw}
                </Typography>

                <Box
                  display="flex"
                  flexWrap="wrap"
                  marginTop="8px"
                  color={theme.palette.text.secondary}
                  sx={{
                    [device.mobile]: {
                      marginTop: 0,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      marginRight: '32px',
                      [device.mobile]: {
                        fontSize: '10px',
                      },
                    }}
                  >
                    {result.cf_department?.raw}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      marginRight: '32px',
                      [device.mobile]: {
                        fontSize: '10px',
                      },
                    }}
                  >
                    {result.cf_brand?.raw}
                  </Typography>

                  {!isMobile ? (
                    <React.Fragment>
                      <Typography
                        variant="subtitle1"
                        sx={{ marginRight: '32px' }}
                      >
                        {result.location_city?.raw}
                      </Typography>

                      <Typography variant="subtitle1">
                        {result.type_of_employment_label.raw}
                      </Typography>
                    </React.Fragment>
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ paddingLeft: '16px' }}>
                <img src={Img} />
              </Box>
            </Box>
          )}
        />
      ) : (
        <Box mt={3}>
          <Typography variant="h3" color={theme.palette.text.secondary}>
            {t('Leider haben wir keine Stelle gefunden.')}
          </Typography>
        </Box>
      )}
    </ResultsStyled>
  );
};
